/**
 * Share a PlayFirst Parent "Today's Play Idea" as a friendly one-page PDF
 * via the Web Share API.
 *
 * Same pattern as the therapist `shareActivityPdf.js` helper, but talks to
 * the parent-app endpoint so the PDF carries the parent's child name and
 * the parent-app branding.
 *
 * Returns: 'shared' | 'downloaded' | 'cancelled' | 'error'.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export async function shareParentPlayIdeaPdf({ playIdeaId, activityName, childFirstName }) {
  if (!playIdeaId) return 'error';
  const token = localStorage.getItem('token');
  if (!token) return 'error';

  let blob;
  try {
    const resp = await axios.get(
      `${API}/parent-app/play-ideas/${playIdeaId}/pdf`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      },
    );
    blob = resp.data;
  } catch (e) {
    return 'error';
  }

  const safeName = (activityName || 'play_idea')
    .replace(/[^A-Za-z0-9_-]+/g, '_')
    .slice(0, 50) || 'play_idea';
  const filename = `PlayFirst_${safeName}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const shareTitle = activityName || "Today's play idea";
  const shareText = childFirstName
    ? `Here's today's play idea for ${childFirstName} — from PlayFirst Parent.`
    : `Here's a play idea from PlayFirst Parent.`;

  try {
    if (
      typeof navigator !== 'undefined'
      && navigator.canShare
      && navigator.canShare({ files: [file] })
      && navigator.share
    ) {
      try {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
        return 'shared';
      } catch (err) {
        if (err && (err.name === 'AbortError' || /cancel|abort/i.test(err.message || ''))) {
          return 'cancelled';
        }
      }
    }
  } catch {}

  try {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return 'downloaded';
  } catch {
    return 'error';
  }
}
